import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MarketingNav, MarketingFooter } from "@/components/marketing/MarketingChrome";
import { Button } from "@/components/ui/button";
import { NUTT_PHOTO, STUDENT_PHOTOS } from "@/lib/assets";
import { OverviewVideo } from "@/components/marketing/OverviewVideo";
import { DOMAINS, SKILLS, LEVELS } from "@/lib/framework";
import {
  ArrowRight, BarChart3, Compass, ShieldCheck, Sparkles, Layers, Users, Building2, LineChart,
} from "lucide-react";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

export default function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <MarketingNav />

      {/* HERO */}
      <section className="brand-hero relative overflow-hidden pt-28 pb-20 text-white">
        <div className="brand-mesh absolute inset-0" />
        <div className="hero-noise pointer-events-none absolute inset-0" />
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.span variants={item} className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-[#a8e063]">
              <Sparkles className="h-3.5 w-3.5" /> Lone Star College · GPA+ Initiative
            </motion.span>
            <motion.h1 variants={item} className="mt-6 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
              Beyond the GPA.<br />
              Measure the skills that <span className="text-[#78be21]">last a lifetime</span>.
            </motion.h1>
            <motion.p variants={item} className="mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
              Work is being rewritten in real time. As AI and automation take over routine tasks, the abilities that
              set people apart are distinctly human — how you <strong className="text-white">communicate, think critically,
              collaborate, and adapt</strong>. These durable skills matter more than ever, yet a GPA can't show a single
              one of them. GPA+ turns everyday coursework into a research-backed <strong className="text-white">Transferable
              Skills Benchmark</strong> — 10 durable skills across 3 domains — so students, advisors, and employers can
              see them, grow them, and verify real career readiness.
            </motion.p>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-3">
              <Link to="/login">
                <Button data-testid="hero-try-demo" size="lg" className="rounded-full bg-[#78be21] px-7 text-base font-bold text-[#00243f] hover:bg-[#6aac1c] accent-glow">
                  Try the working demo <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/framework">
                <Button data-testid="hero-learn-more" size="lg" variant="outline" className="rounded-full border-white/30 bg-white/5 px-7 text-base font-bold text-white hover:bg-white/10">
                  Explore the framework
                </Button>
              </Link>
            </motion.div>
            <motion.div variants={item} className="mt-8 flex flex-wrap gap-6 text-sm text-slate-400">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#78be21]" /> FERPA-aligned RBAC</span>
              <span className="flex items-center gap-2"><Layers className="h-4 w-4 text-[#78be21]" /> D2L/Brightspace-ready</span>
            </motion.div>
          </motion.div>

          <OverviewVideo />
        </div>
      </section>

      {/* WHAT IS GPA+ */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5fa114]">What is GPA+</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-[#003057] sm:text-4xl">
            A GPA tells you a grade. GPA+ tells you what a learner can <span className="text-[#78be21]">do</span>.
          </h2>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">
            The Transferable Skills Benchmark (TSB) maps course learning outcomes onto ten durable skills,
            aggregates them into three capacity domains, and compares each profile to real career-readiness demands.
          </p>
        </div>
        <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
          {[
            { icon: BarChart3, t: "Real scoring engine", d: "Course SLOs are weight-mapped to skills, rolled up to domains, then to a 1.0–4.0 TSB (also 0–100)." },
            { icon: Compass, t: "Career alignment", d: "Every profile is matched against 18 careers' skill-demand profiles for alignment %, strengths and gaps." },
            { icon: ShieldCheck, t: "Employer-verified", d: "A separate 360 signal, individually weighted, labeled, traceable and reversible — never silent." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl border border-slate-200 bg-white p-6 card-elevate">
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#003057]/6">
                <c.icon className="h-5 w-5 text-[#003057]" />
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold text-[#003057]">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DOMAINS + SKILLS */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5fa114]">The Framework</p>
          <h2 className="mt-3 font-heading text-3xl font-bold tracking-tight text-[#003057] sm:text-4xl">
            10 skills · 3 domains
          </h2>
          <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-3">
            {DOMAINS.map((d) => (
              <div key={d.id} className="rounded-2xl border-t-4 bg-white p-6 card-elevate" style={{ borderColor: d.color }}>
                <h3 className="font-heading text-lg font-bold text-[#003057]">{d.name}</h3>
                <ul className="mt-4 space-y-2">
                  {SKILLS.filter((s) => s.domain === d.id).map((s) => (
                    <li key={s.id} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: d.color }} /> {s.name}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            {LEVELS.map((lv) => (
              <div key={lv.id} className={`rounded-full border px-4 py-1.5 text-sm font-semibold ${lv.badge}`}>
                {lv.name} · {lv.min.toFixed(1)}–{lv.max.toFixed(lv.id === "advanced" ? 1 : 2)}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCES */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
        <h2 className="font-heading text-3xl font-bold tracking-tight text-[#003057] sm:text-4xl">Built for four audiences</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Users, t: "Students", d: "See your TSB, run what-if degree changes, and get AI coaching on career fit." },
            { icon: Compass, t: "Advisors", d: "View assigned students, spot skill gaps, and plan targeted interventions." },
            { icon: LineChart, t: "Administrators", d: "Cohort distributions, program comparisons, and equity-gap analytics." },
            { icon: Building2, t: "Employers", d: "Browse opt-in talent and submit verified 360 reviews on interns." },
          ].map((c) => (
            <div key={c.t} className="rounded-2xl bg-[#003057] p-6 text-white card-elevate">
              <c.icon className="h-8 w-8 text-[#78be21]" />
              <h3 className="mt-4 font-heading text-lg font-bold">{c.t}</h3>
              <p className="mt-2 text-sm text-slate-300">{c.d}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 flex justify-center">
          <Link to="/login">
            <Button size="lg" className="rounded-full bg-[#78be21] px-8 text-base font-bold text-[#00243f] hover:bg-[#6aac1c]">
              Explore the demo <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* REAL STUDENTS */}
      <section className="bg-white py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5fa114]">Real students. Real skills.</p>
            <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-[#003057] sm:text-4xl">
              Every Lone Star College student has a story a GPA can't tell
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Behind every transcript is a person who communicates, leads, solves, and adapts. GPA+ makes those
              durable, transferable skills visible — so students can prove who they are, not just what they scored.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4 lg:grid-rows-2">
            <motion.figure initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
              className="group relative col-span-2 row-span-2 overflow-hidden rounded-3xl shadow-lg" data-testid="student-photo">
              <img src={STUDENT_PHOTOS.stairs} alt="Two Lone Star College students talking on campus stairs" className="h-full min-h-[280px] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-[#00243f]/90 to-transparent" />
              <figcaption className="absolute inset-x-5 bottom-5">
                <span className="rounded-full bg-[#78be21] px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#00243f]">Communication</span>
                <p className="mt-2 font-heading text-xl font-bold text-white">Sharing ideas, listening, connecting.</p>
              </figcaption>
            </motion.figure>

            {[
              { key: "group", skill: "Teamwork", alt: "Three Lone Star College students standing together" },
              { key: "lab", skill: "Critical Thinking", alt: "Lone Star College student working in a science lab" },
              { key: "phone", skill: "Problem-Solving", alt: "Lone Star College students working on a car engine" },
              { key: "books", skill: "Collaboration", alt: "Two Lone Star College students studying together with books" },
            ].map((p, i) => (
              <motion.figure key={p.key} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.08 * (i + 1) }}
                className="group relative overflow-hidden rounded-2xl shadow-md" data-testid="student-photo">
                <img src={STUDENT_PHOTOS[p.key]} alt={p.alt} className="h-full min-h-[132px] w-full object-cover object-top transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[#00243f]/85 to-transparent" />
                <figcaption className="absolute inset-x-3 bottom-3">
                  <span className="rounded-full bg-white/95 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-[#003057]">{p.skill}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-400">Photos courtesy of Lone Star College.</p>
        </div>
      </section>

      {/* FOUNDER */}
      <section className="bg-slate-50 py-20">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#5fa114]">The vision behind GPA+</p>
          <div className="mt-6 grid grid-cols-1 gap-8 md:grid-cols-[auto,1fr] md:items-center">
            <div className="relative h-44 w-44 overflow-hidden rounded-2xl border-4 border-white shadow-xl accent-glow">
              <img src={NUTT_PHOTO} alt="Dr. Lee Ann Nutt" className="h-full w-full object-cover" data-testid="founder-photo" />
            </div>
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-[#78be21]/15 px-3 py-1 text-xs font-bold uppercase tracking-widest text-[#5fa114]">
                Visionary behind GPA+
              </span>
              <h2 className="mt-3 font-heading text-2xl font-bold text-[#003057] sm:text-3xl">Dr. Lee Ann Nutt</h2>
              <p className="mt-1 text-sm font-semibold text-slate-500">President, Lone Star College–Tomball</p>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                GPA+ is the brainchild of Dr. Lee Ann Nutt. A lifelong community-college leader with more than three
                decades in higher education, she holds a bachelor's, master's, and doctorate from Texas Tech University,
                with doctoral work in community college leadership.
              </p>
              <p className="mt-3 text-lg leading-relaxed text-slate-600">
                She started this project on a simple conviction: a transcript's GPA doesn't capture the durable,
                transferable skills that truly prepare students for the workforce. GPA+ exists to make those skills
                <span className="font-semibold text-[#003057]"> visible, measurable, and actionable</span> — for
                students, faculty, and the employers who hire them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST / COMPLIANCE STRIP */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-6 sm:px-6 lg:flex-row lg:justify-between lg:px-8">
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm font-semibold text-slate-600">
            {[
              { icon: ShieldCheck, t: "FERPA-aligned" },
              { icon: ShieldCheck, t: "THECB-aligned" },
              { icon: ShieldCheck, t: "Opt-in consent" },
              { icon: ShieldCheck, t: "Full audit trail" },
              { icon: ShieldCheck, t: "US-based · TLS encrypted" },
            ].map((c) => (
              <span key={c.t} className="flex items-center gap-2" data-testid="trust-item">
                <c.icon className="h-4 w-4 text-[#78be21]" /> {c.t}
              </span>
            ))}
          </div>
          <Link to="/privacy" data-testid="trust-privacy-link" className="text-sm font-bold text-[#005291] hover:underline">
            Read our Privacy & Compliance →
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
